import React, { useEffect, useState } from 'react'
import { authFetch } from '../api/authFetch'
import InlineAlert from '../components/InlineAlert'

function formatStatusLabel(status) {
    if (status === 'open') return 'Received'
    if (status === 'answered') return 'Answered'
    if (status === 'closed') return 'Closed'
    return status
}

function statusBadgeClass(status) {
    if (status === 'answered') return 'bg-green-100 text-green-800'
    if (status === 'closed') return 'bg-gray-200 text-gray-700'
    return 'bg-amber-50 text-amber-900'
}

function formatUserCell(chat) {
    if (!chat) return '—'
    const name = [chat.firstName, chat.lastName].filter(Boolean).join(' ')
    const line1 = name || '—'
    const line2 = chat.username ? `@${chat.username}` : '(no username)'
    return { line1, line2 }
}

export default function Feedbacks() {
    const [feedbacks, setFeedbacks] = useState([])
    const [loading, setLoading] = useState(true)
    const [expandedId, setExpandedId] = useState(null)
    const [threads, setThreads] = useState({})
    const [threadLoading, setThreadLoading] = useState(null)
    const [replyText, setReplyText] = useState({})
    const [replyBusy, setReplyBusy] = useState(null)
    const [statusUpdating, setStatusUpdating] = useState(false)
    const [error, setError] = useState(null)
    const [success, setSuccess] = useState(null)

    const fetchList = async () => {
        setLoading(true)
        setError(null)
        try {
            const res = await authFetch('/api/feedbacks')
            const data = await res.json()
            if (!res.ok) throw new Error(data.error || 'Failed to load')
            setFeedbacks(data.feedbacks || [])
        } catch (e) {
            console.error(e)
            setError(e.message || 'Failed to load feedbacks')
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchList()
    }, [])

    const loadThread = async (rootId) => {
        setThreadLoading(rootId)
        setError(null)
        try {
            const res = await authFetch(`/api/feedbacks/${rootId}/thread`)
            const data = await res.json()
            if (!res.ok) throw new Error(data.error || 'Failed to load thread')
            setThreads((prev) => ({ ...prev, [rootId]: data.messages || [] }))
        } catch (e) {
            console.error(e)
            setError(e.message || 'Failed to load thread')
        } finally {
            setThreadLoading(null)
        }
    }

    const toggleExpand = (rootId) => {
        if (expandedId === rootId) {
            setExpandedId(null)
            return
        }
        setExpandedId(rootId)
        if (!threads[rootId]) {
            loadThread(rootId)
        }
    }

    const handleReply = async (rootId) => {
        const text = (replyText[rootId] || '').trim()
        if (!text) {
            setError('Enter a message to send.')
            return
        }
        setReplyBusy(rootId)
        setError(null)
        try {
            const res = await authFetch(`/api/feedbacks/${rootId}/reply`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ text, statusAfter: 'answered' })
            })
            const data = await res.json().catch(() => ({}))
            if (!res.ok) throw new Error(data.error || 'Failed to send')
            setReplyText((prev) => ({ ...prev, [rootId]: '' }))
            setSuccess('Reply sent.')
            await fetchList()
            await loadThread(rootId)
        } catch (e) {
            console.error(e)
            setError(e.message || 'Failed to send reply')
        } finally {
            setReplyBusy(null)
        }
    }

    const patchStatus = async (rootId, status) => {
        setStatusUpdating(true)
        setError(null)
        try {
            const res = await authFetch(`/api/feedbacks/${rootId}/status`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ status })
            })
            const data = await res.json().catch(() => ({}))
            if (!res.ok) throw new Error(data.error || 'Failed to update')
            setSuccess('Status updated.')
            await fetchList()
            if (threads[rootId]) {
                await loadThread(rootId)
            }
        } catch (e) {
            console.error(e)
            setError(e.message || 'Failed to update status')
        } finally {
            setStatusUpdating(false)
        }
    }

    return (
        <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-6">Feedbacks</h1>

            {success && (
                <InlineAlert variant="success" onDismiss={() => setSuccess(null)}>
                    <p>{success}</p>
                </InlineAlert>
            )}
            {error && (
                <InlineAlert variant="error" onDismiss={() => setError(null)}>
                    <p>{error}</p>
                </InlineAlert>
            )}

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                {loading ? (
                    <div className="flex items-center justify-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                    <th className="w-12 px-4 py-3" aria-hidden="true" />
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        Date
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        User
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        Message
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        Status
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {feedbacks.map((fb) => (
                                    <React.Fragment key={fb.id}>
                                        <tr className="hover:bg-gray-50 align-top">
                                            <td className="px-4 py-4 text-center">
                                                <button
                                                    type="button"
                                                    onClick={() => toggleExpand(fb.id)}
                                                    className="cursor-pointer text-indigo-500 hover:text-indigo-700 p-1"
                                                    aria-expanded={expandedId === fb.id}
                                                    title={expandedId === fb.id ? 'Collapse' : 'Open thread'}
                                                >
                                                    {expandedId === fb.id ? '▲' : '▼'}
                                                </button>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-600 whitespace-nowrap">
                                                {new Date(fb.updatedAt).toLocaleString()}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-900">
                                                {(() => {
                                                    const u = formatUserCell(fb.chat)
                                                    return (
                                                        <>
                                                            {u.line1}
                                                            <br />
                                                            <span className="text-gray-500">{u.line2}</span>
                                                        </>
                                                    )
                                                })()}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-900 max-w-md">
                                                <div className="whitespace-pre-wrap break-words">{fb.message}</div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span
                                                    className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${statusBadgeClass(fb.status)}`}
                                                >
                                                    {formatStatusLabel(fb.status)}
                                                </span>
                                            </td>
                                        </tr>
                                        {expandedId === fb.id && (
                                            <tr className="bg-gray-50">
                                                <td colSpan={5} className="px-6 py-4">
                                                    <div className="max-w-4xl space-y-4">
                                                        {threadLoading === fb.id && (
                                                            <p className="text-sm text-gray-500">Loading thread…</p>
                                                        )}
                                                        {threads[fb.id] && (
                                                            <ul className="space-y-3 border border-gray-200 rounded-lg p-4 bg-white">
                                                                {threads[fb.id].map((m) => (
                                                                    <li
                                                                        key={m.id}
                                                                        className="text-sm border-b border-gray-100 last:border-0 pb-3 last:pb-0"
                                                                    >
                                                                        <div className="flex justify-between gap-2 text-xs text-gray-500 mb-1">
                                                                            <span className="font-medium text-gray-700">
                                                                                {m.author === 'admin'
                                                                                    ? 'Admin'
                                                                                    : 'User'}
                                                                                {m.category
                                                                                    ? ` · ${m.category}`
                                                                                    : ''}
                                                                            </span>
                                                                            <span>
                                                                                {new Date(m.createdAt).toLocaleString()}
                                                                            </span>
                                                                        </div>
                                                                        <div className="whitespace-pre-wrap break-words text-gray-900">
                                                                            {m.message}
                                                                        </div>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        )}
                                                        <div className="flex flex-wrap gap-2 items-center">
                                                            <button
                                                                type="button"
                                                                onClick={() => patchStatus(fb.id, 'answered')}
                                                                disabled={statusUpdating}
                                                                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                                                            >
                                                                Mark answered
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => patchStatus(fb.id, 'closed')}
                                                                disabled={statusUpdating}
                                                                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                                                            >
                                                                Close
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => patchStatus(fb.id, 'open')}
                                                                disabled={statusUpdating}
                                                                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                                                            >
                                                                Mark received
                                                            </button>
                                                        </div>
                                                        <div>
                                                            <label className="block text-xs font-medium text-gray-500 mb-1">
                                                                Reply in Telegram
                                                            </label>
                                                            <textarea
                                                                value={replyText[fb.id] || ''}
                                                                onChange={(e) =>
                                                                    setReplyText((prev) => ({
                                                                        ...prev,
                                                                        [fb.id]: e.target.value
                                                                    }))
                                                                }
                                                                rows={4}
                                                                className="w-full max-w-xl px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-y"
                                                                placeholder="Type a reply to the user…"
                                                                disabled={replyBusy === fb.id}
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={() => handleReply(fb.id)}
                                                                disabled={replyBusy === fb.id}
                                                                className="mt-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 disabled:opacity-50"
                                                            >
                                                                {replyBusy === fb.id ? 'Sending…' : 'Send reply'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                        {feedbacks.length === 0 && (
                            <p className="px-6 py-12 text-center text-gray-500 text-sm">No feedback threads yet.</p>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}
